//----------------------------------------------------------------------------------------------------------------------
// Debug Console
//
// Manages a namespace object on `window` that exposes game engine internals to the browser dev console.
// Supports direct values, callable commands, and dynamic getters via Object.defineProperty.
//----------------------------------------------------------------------------------------------------------------------

import type { Disposable } from '../interfaces/lifecycle.ts';

//----------------------------------------------------------------------------------------------------------------------

interface DynamicGetter
{
    get : () => unknown;
}

interface ExposedEntry
{
    getter ?: () => unknown;
    value ?: unknown;
}

//----------------------------------------------------------------------------------------------------------------------

export class DebugConsole implements Disposable
{
    private _namespaceName : string;
    private _entries = new Map<string, ExposedEntry>();
    private _disposed = false;

    constructor(namespaceName = 'sage')
    {
        this._namespaceName = namespaceName;
        this._installNamespace();
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    expose(name : string, value : unknown | DynamicGetter) : void
    {
        if(this._disposed)
        {
            return;
        }

        if(this._isDynamicGetter(value))
        {
            this._entries.set(name, { getter: value.get });
            this._defineGetter(name, value.get);
        }
        else
        {
            this._entries.set(name, { value });
            this._defineValue(name, value);
        }
    }

    setNamespace(name : string) : void
    {
        if(this._disposed || name === this._namespaceName)
        {
            return;
        }

        this._removeNamespace();
        this._namespaceName = name;
        this._installNamespace();

        for(const [ entryName, entry ] of this._entries)
        {
            if(entry.getter)
            {
                this._defineGetter(entryName, entry.getter);
            }
            else
            {
                this._defineValue(entryName, entry.value);
            }
        }
    }

    get namespaceName() : string
    {
        return this._namespaceName;
    }

    dispose() : void
    {
        if(this._disposed)
        {
            return;
        }

        this._removeNamespace();
        this._entries.clear();
        this._disposed = true;
    }

    async $teardown() : Promise<void>
    {
        this.dispose();
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private
    //------------------------------------------------------------------------------------------------------------------

    private _isDynamicGetter(value : unknown) : value is DynamicGetter
    {
        return typeof value === 'object'
            && value !== null
            && 'get' in value
            && typeof (value as DynamicGetter).get === 'function';
    }

    private _getNamespace() : Record<string, unknown> | undefined
    {
        return Reflect.get(window, this._namespaceName) as Record<string, unknown> | undefined;
    }

    private _installNamespace() : void
    {
        if(typeof window === 'undefined')
        {
            return;
        }

        Reflect.set(window, this._namespaceName, {});
    }

    private _removeNamespace() : void
    {
        if(typeof window === 'undefined')
        {
            return;
        }

        Reflect.deleteProperty(window, this._namespaceName);
    }

    private _defineGetter(name : string, getter : () => unknown) : void
    {
        const ns = this._getNamespace();
        if(!ns)
        {
            return;
        }

        Object.defineProperty(ns, name, {
            get: getter,
            enumerable: true,
            configurable: true,
        });
    }

    private _defineValue(name : string, value : unknown) : void
    {
        const ns = this._getNamespace();
        if(!ns)
        {
            return;
        }

        Object.defineProperty(ns, name, {
            value,
            writable: true,
            enumerable: true,
            configurable: true,
        });
    }
}

//----------------------------------------------------------------------------------------------------------------------
